/**
 * src/shell/save/validate.ts — 存档导入校验闸门（设计稿 §5.1.6）。
 *
 * 四道关卡，任一失败拒绝导入（人话错误，技术细节进控制台）：
 * 1. JSON 合法 + meta.game === 'third-play' + bundleVersion 兼容 + 7 键齐全；
 * 2. schema 校验：employee/project/decision/review 走 contracts 原文（schema 是法律），
 *    save/market 走内嵌结构校验（无独立 schema 文件）；
 * 3. 跨字段不变量（对齐旧 validate_contracts.py 业务规则）：
 *    rarity ↔ cost_coefficient、token total 自洽、决策恰 3 选项且三色各一、
 *    五维权重齐全且和为 1、decision_ids ≤ 2；
 * 4. 引用完整性：team 员工存在、decision.project_id 存在、
 *    非 completed 项目的 decision_ids/review_id 必须可解析——
 *    completed 项目的 review_id 允许悬空（镇店存档 proj_msckatc8 即此形状，
 *    设计资产零改动；新完成的游戏评审必然同包落盘，不受影响）。
 */

import type {
  Decision,
  Employee,
  Project,
  Review,
  Rarity,
  SaveBundle,
} from '../../gm'
import decisionSchema from '../../../../contracts/decision.schema.json'
import employeeSchema from '../../../../contracts/employee.schema.json'
import projectSchema from '../../../../contracts/project.schema.json'
import reviewSchema from '../../../../contracts/review.schema.json'
import { validateJsonSchema } from './schema-validator'
import type { CandidatesFile } from './seed'
import type { BundleKey } from './keys'

const RARITY_COST: Record<Rarity, number> = { rookie: 1.0, veteran: 1.8, legend: 3.0 }
const DIMENSIONS = ['completion', 'creativity', 'art', 'game_feel', 'theme_fit'] as const

export interface ImportFileShape {
  meta: { game: string; bundleVersion: number; exportedAt?: string; appVersion?: string }
  save: SaveBundle['save']
  projects: SaveBundle['projects']
  decisions: SaveBundle['decisions']
  reviews: SaveBundle['reviews']
  employees: SaveBundle['employees']
  market: SaveBundle['market']
  candidates: CandidatesFile
}

export type ValidationResult =
  | { ok: true; data: ImportFileShape }
  | { ok: false; errors: string[] }

export function validateImport(raw: unknown): ValidationResult {
  const errors: string[] = []
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ok: false, errors: ['文件不是合法的存档包（顶层应为对象）。'] }
  }
  const file = raw as Record<string, unknown>

  // ── 关卡 1：meta 与键齐全 ──
  const meta = file.meta as Record<string, unknown> | undefined
  if (!meta || meta.game !== 'third-play') {
    return { ok: false, errors: ['这不是《第三次玩》的存档包（meta.game 不符）。'] }
  }
  if (meta.bundleVersion !== 1) {
    return { ok: false, errors: [`存档包版本 ${String(meta.bundleVersion)} 与当前版本不兼容。`] }
  }
  const requiredKeys: Array<BundleKey | 'candidates'> = [
    'save',
    'projects',
    'decisions',
    'reviews',
    'employees',
    'market',
    'candidates',
  ]
  for (const k of requiredKeys) {
    if (!(k in file)) errors.push(`存档包缺少「${k}」键。`)
  }
  if (errors.length > 0) return { ok: false, errors }

  const bundle = {
    save: file.save,
    projects: file.projects,
    decisions: file.decisions,
    reviews: file.reviews,
    employees: file.employees,
    market: file.market,
  } as SaveBundle
  const candidates = file.candidates as CandidatesFile

  // ── 关卡 2：schema 校验（contracts 原文） ──
  const projects = (bundle.projects as { projects?: unknown[] }).projects
  const decisions = (bundle.decisions as { decisions?: unknown[] }).decisions
  const reviews = (bundle.reviews as { reviews?: unknown[] }).reviews
  const employees = (bundle.employees as { employees?: unknown[] }).employees
  const cands = (candidates as { candidates?: unknown[] }).candidates

  if (!Array.isArray(projects)) errors.push('projects 应为 { projects: [...] } wrapper。')
  if (!Array.isArray(decisions)) errors.push('decisions 应为 { decisions: [...] } wrapper。')
  if (!Array.isArray(reviews)) errors.push('reviews 应为 { reviews: [...] } wrapper。')
  if (!Array.isArray(employees)) errors.push('employees 应为 { employees: [...] } wrapper。')
  if (!Array.isArray(cands)) errors.push('candidates 应为 { candidates: [...] } wrapper。')
  if (errors.length > 0) return { ok: false, errors }

  projects!.forEach((p, i) =>
    errors.push(...validateJsonSchema(p, projectSchema as Record<string, unknown>).map((e) => `项目[${i}] ${e}`)),
  )
  decisions!.forEach((d, i) =>
    errors.push(...validateJsonSchema(d, decisionSchema as Record<string, unknown>).map((e) => `决策[${i}] ${e}`)),
  )
  reviews!.forEach((r, i) =>
    errors.push(...validateJsonSchema(r, reviewSchema as Record<string, unknown>).map((e) => `评审[${i}] ${e}`)),
  )
  employees!.forEach((e2, i) =>
    errors.push(...validateJsonSchema(e2, employeeSchema as Record<string, unknown>).map((e) => `员工[${i}] ${e}`)),
  )
  cands!.forEach((c, i) =>
    errors.push(...validateJsonSchema(c, employeeSchema as Record<string, unknown>).map((e) => `候选[${i}] ${e}`)),
  )

  // save / market 内嵌结构校验（无独立 schema 文件）
  errors.push(...checkSaveShape(bundle.save as unknown as Record<string, unknown>))
  errors.push(...checkMarketShape(bundle.market as unknown as Record<string, unknown>))
  if (errors.length > 0) return { ok: false, errors }

  // ── 关卡 3：跨字段不变量 ──
  const allPeople = [...(employees as Employee[]), ...(cands as Employee[])]
  for (const p of allPeople) {
    if (RARITY_COST[p.rarity] !== p.cost_coefficient) {
      errors.push(`员工 ${p.name ?? p.id} 的稀有度 ${p.rarity} 与成本系数 ${p.cost_coefficient} 不一致。`)
    }
  }
  for (const p of projects as Project[]) {
    const t = p.token_usage
    if (t && t.total !== t.designer + t.programmer + t.artist) {
      errors.push(`项目 ${p.id} 的 token_usage.total 与三岗之和不符。`)
    }
    if (p.decision_ids && p.decision_ids.length > 2) {
      errors.push(`项目 ${p.id} 的 decision_ids 超过 2 个上限。`)
    }
  }
  for (const d of decisions as Decision[]) {
    const styles = d.options.map((o) => o.style).sort()
    if (d.options.length !== 3 || styles.join(',') !== 'bold,safe,signature') {
      errors.push(`决策 ${d.id} 必须恰 3 个选项且 safe/bold/signature 三色各一。`)
    }
  }
  const market = bundle.market as unknown as { dimension_weights: Record<string, number> }
  const wsum = DIMENSIONS.reduce((acc, k) => acc + (market.dimension_weights?.[k] ?? NaN), 0)
  if (Math.abs(wsum - 1) > 1e-9) {
    errors.push(`market.dimension_weights 五键必须齐全且和为 1（当前和 ${String(wsum)}）。`)
  }
  if (errors.length > 0) return { ok: false, errors }

  // ── 关卡 4：引用完整性 ──
  const peopleIds = new Set(allPeople.map((p) => p.id))
  const projectIds = new Set((projects as Project[]).map((p) => p.id))
  const decisionIds = new Set((decisions as Decision[]).map((d) => d.id))
  const reviewIds = new Set((reviews as Review[]).map((r) => r.id))
  for (const p of projects as Project[]) {
    for (const role of ['designer', 'programmer', 'artist'] as const) {
      if (!peopleIds.has(p.team[role])) {
        errors.push(`项目 ${p.id} 团队的 ${role}（${p.team[role]}）在员工与候选中都找不到。`)
      }
    }
    if (p.status !== 'completed') {
      for (const did of p.decision_ids) {
        if (!decisionIds.has(did)) errors.push(`在研项目 ${p.id} 引用了不存在的决策 ${did}。`)
      }
      if (p.review_id !== null && !reviewIds.has(p.review_id)) {
        errors.push(`在研项目 ${p.id} 引用了不存在的评审 ${p.review_id}。`)
      }
    }
  }
  for (const d of decisions as Decision[]) {
    if (!projectIds.has(d.project_id)) errors.push(`决策 ${d.id} 指向不存在的项目 ${d.project_id}。`)
  }
  for (const r of reviews as Review[]) {
    if (!projectIds.has(r.project_id)) errors.push(`评审 ${r.id} 指向不存在的项目 ${r.project_id}。`)
  }
  if (errors.length > 0) return { ok: false, errors }

  return { ok: true, data: {
    meta: meta as ImportFileShape['meta'],
    save: bundle.save,
    projects: bundle.projects,
    decisions: bundle.decisions,
    reviews: bundle.reviews,
    employees: bundle.employees,
    market: bundle.market,
    candidates,
  } }
}

function checkSaveShape(save: Record<string, unknown>): string[] {
  const errors: string[] = []
  if (save === null || typeof save !== 'object' || Array.isArray(save)) {
    return ['save 应为对象。']
  }
  const req: Array<[string, string]> = [
    ['company_name', 'string'],
    ['player_title', 'string'],
    ['level', 'number'],
    ['exp', 'number'],
    ['funds', 'number'],
    ['total_token_cost', 'number'],
    ['projects_completed', 'number'],
  ]
  for (const [k, t] of req) {
    if (typeof save[k] !== t) errors.push(`save.${k} 应为 ${t}。`)
  }
  const bailout = save.bailout as Record<string, unknown> | undefined
  if (!bailout || typeof bailout.threshold !== 'number' || typeof bailout.grant_to !== 'number' || typeof bailout.times_used !== 'number') {
    errors.push('save.bailout 结构不完整（threshold/grant_to/times_used）。')
  }
  if (!Array.isArray(save.level_unlocks)) errors.push('save.level_unlocks 应为数组。')
  return errors
}

function checkMarketShape(market: Record<string, unknown>): string[] {
  const errors: string[] = []
  if (market === null || typeof market !== 'object' || Array.isArray(market)) {
    return ['market 应为对象。']
  }
  const reqStr = ['season', 'editorial']
  for (const k of reqStr) {
    if (typeof market[k] !== 'string') errors.push(`market.${k} 应为 string。`)
  }
  const reqNum = ['base_market', 'revenue_exponent', 'token_to_coin_rate']
  for (const k of reqNum) {
    if (typeof market[k] !== 'number') errors.push(`market.${k} 应为 number。`)
  }
  for (const k of ['hot_themes', 'cold_themes', 'hot_mechanics']) {
    if (!Array.isArray(market[k])) errors.push(`market.${k} 应为数组。`)
  }
  return errors
}
